import type { Ticket } from "@sd/types/ticket";
import { normalizeStatus } from "@sd/constants/ticketStatuses";
import type { StreamTicket } from "@sd/lib/ticketStreamUtils";

/** Apply optimistic agent-panel patches onto a base ticket (list or detail API). */
export function mergeAgentTicketPatch(
  base: Ticket,
  patch?: Partial<Ticket>
): Ticket {
  if (!patch) return base;
  return {
    ...base,
    ...patch,
    notes: patch.notes ?? base.notes,
    slaTimers: patch.slaTimers ?? base.slaTimers,
    attachments:
      patch.attachments !== undefined ? patch.attachments : base.attachments,
    attachmentFileIds:
      patch.attachmentFileIds !== undefined
        ? patch.attachmentFileIds
        : base.attachmentFileIds,
  };
}

type AssigneeOption = { id: string; name: string };

function assigneeMatchesApi(
  patchAssignee: string,
  apiAssignee: string | null | undefined,
  options: AssigneeOption[]
): boolean {
  const api = apiAssignee?.trim();
  if (!api) return false;
  if (api === patchAssignee) return true;
  const opt = options.find(a => a.name === patchAssignee || a.id === patchAssignee);
  return Boolean(opt && (api === opt.id || api === opt.name));
}

/**
 * Drop override fields once the list API row has caught up.
 * Status/priority/VIP must not be wiped on list refetch — grid data often lags
 * behind ticket_create PATCH, which made STREAM show a different status than detail.
 */
export function pruneAgentTicketOverride(
  patch: Partial<Ticket>,
  row: StreamTicket | undefined,
  assigneeOptions: AssigneeOption[]
): Partial<Ticket> | null {
  const next: Partial<Ticket> = {};

  if (patch.assignee !== undefined) {
    if (!assigneeMatchesApi(patch.assignee, row?.assignedAgent, assigneeOptions)) {
      next.assignee = patch.assignee;
    }
  }

  if (patch.status !== undefined) {
    if (!row || normalizeStatus(row.status) !== normalizeStatus(patch.status)) {
      next.status = patch.status;
    }
  }

  if (patch.priorityId !== undefined) {
    if (!row || String(row.priorityId ?? "") !== String(patch.priorityId)) {
      next.priorityId = patch.priorityId;
      if (patch.priorityLabel !== undefined) next.priorityLabel = patch.priorityLabel;
      if (patch.priority !== undefined) next.priority = patch.priority;
    }
  } else if (patch.priority !== undefined) {
    if (!row || String(row.priority) !== String(patch.priority)) {
      next.priority = patch.priority;
      if (patch.priorityLabel !== undefined) next.priorityLabel = patch.priorityLabel;
    }
  }

  if (patch.isVip !== undefined && (!row || row.isVip !== patch.isVip)) {
    next.isVip = patch.isVip;
  }
  if (patch.isSla !== undefined && (!row || row.isSla !== patch.isSla)) {
    next.isSla = patch.isSla;
  }

  return Object.keys(next).length > 0 ? next : null;
}

/** STREAM / overrides win over stale ticket_create detail for live fields. */
export function overlayLiveTicketFields(
  detail: Ticket,
  live: Ticket | null | undefined
): Ticket {
  if (!live) return detail;
  return {
    ...detail,
    status: live.status,
    assignee: live.assignee ?? detail.assignee,
    priority: live.priority,
    priorityId: live.priorityId ?? detail.priorityId,
    priorityLabel: live.priorityLabel ?? detail.priorityLabel,
    isVip: live.isVip ?? detail.isVip,
    isSla: live.isSla ?? detail.isSla,
    title: live.title || detail.title,
  };
}

function overrideFingerprint(map: Record<string, Partial<Ticket>>): string {
  const ids = Object.keys(map).sort();
  return ids
    .map(id => {
      const p = map[id];
      return [
        id,
        p.assignee ?? "",
        p.status ?? "",
        p.priorityId ?? "",
        p.priority ?? "",
        p.isVip === undefined ? "" : String(p.isVip),
        p.isSla === undefined ? "" : String(p.isSla),
      ].join(":");
    })
    .join("|");
}

export function pruneAgentTicketOverrides(
  prev: Record<string, Partial<Ticket>>,
  rows: StreamTicket[],
  assigneeOptions: AssigneeOption[]
): Record<string, Partial<Ticket>> {
  const kept: Record<string, Partial<Ticket>> = {};
  for (const [id, patch] of Object.entries(prev)) {
    const next = pruneAgentTicketOverride(
      patch,
      rows.find(r => r.id === id),
      assigneeOptions
    );
    if (next) kept[id] = next;
  }
  if (overrideFingerprint(prev) === overrideFingerprint(kept)) {
    return prev;
  }
  return kept;
}

